import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BookHeart, Save, Trash2, CalendarDays } from "lucide-react";
import MoodChart from "@/components/MoodChart";
import JournalReminder from "@/components/JournalReminder";

const STORAGE_KEY = "em_journal_entries_v1";

const moods = [
  { v: "down", label: "💧 Dur", color: "bg-rose-500/15 text-rose-200 border-rose-500/30" },
  { v: "flat", label: "🌫️ Neutre", color: "bg-slate-500/15 text-slate-200 border-slate-500/30" },
  { v: "ok", label: "🙂 OK", color: "bg-amber-500/15 text-amber-200 border-amber-500/30" },
  { v: "good", label: "💪 Bien", color: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" },
  { v: "fire", label: "🔥 Au top", color: "bg-orange-500/15 text-orange-200 border-orange-500/30" },
];

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function loadAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function JournalSection() {
  const [entries, setEntries] = useState(() => loadAll());
  const [date, setDate] = useState(todayIso());

  const current = useMemo(
    () => entries[date] || { mood: "", training: "", meals: "", notes: "" },
    [entries, date]
  );

  const [form, setForm] = useState(current);

  useEffect(() => {
    setForm(current);
  }, [current]);

  const save = () => {
    const next = { ...entries, [date]: { ...form, updated_at: new Date().toISOString() } };
    setEntries(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      toast.success("Journal enregistré sur ton appareil.");
    } catch {
      toast.error("Stockage local plein ou désactivé.");
    }
  };

  const removeDay = () => {
    const next = { ...entries };
    delete next[date];
    setEntries(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setForm({ mood: "", training: "", meals: "", notes: "" });
    toast("Entrée du jour supprimée.");
  };

  const dates = Object.keys(entries).sort().reverse().slice(0, 14);

  return (
    <section id="journal" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <span className="em-eyebrow">Mon Journal</span>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
          Un petit carnet, 100% privé.
        </h2>
        <p className="mt-4 text-slate-300 max-w-2xl">
          Écris ton humeur, ta séance et tes repas. Tout reste sur ton appareil — personne
          d'autre n'y accède. Même pas nous.
        </p>

        <div className="mt-8">
          <JournalReminder />
        </div>

        <div className="mt-4 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 em-card p-6 lg:p-8" data-testid="journal-editor">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
                <BookHeart className="w-5 h-5 text-emerald-300" />
              </div>
              <div className="min-w-0">
                <h3 className="text-xl font-semibold text-white font-[Outfit]">
                  Entrée du {new Date(date).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}
                </h3>
                <p className="text-sm text-slate-400">Stockage navigateur — rien ne quitte ton appareil.</p>
              </div>
              <div className="ml-auto">
                <label className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-2 text-sm">
                  <CalendarDays className="w-4 h-4 text-slate-300" />
                  <input
                    type="date"
                    value={date}
                    max={todayIso()}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="journal-date"
                    className="bg-transparent focus:outline-none text-slate-100"
                  />
                </label>
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm text-slate-300 mb-2">Mon humeur</p>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Humeur">
                {moods.map((m) => (
                  <button
                    key={m.v}
                    type="button"
                    role="radio"
                    aria-checked={form.mood === m.v}
                    onClick={() => setForm({ ...form, mood: m.v })}
                    data-testid={`journal-mood-${m.v}`}
                    className={`px-4 py-2 rounded-full border text-sm font-medium ${m.color} ${
                      form.mood === m.v ? "ring-2 ring-offset-0 ring-white/40" : "opacity-80 hover:opacity-100"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 grid gap-4">
              <div>
                <label className="text-sm text-slate-300">Séance / mouvement</label>
                <textarea
                  rows={2}
                  value={form.training}
                  onChange={(e) => setForm({ ...form, training: e.target.value })}
                  placeholder="Ex : Full-body 45 min, +2 kg au squat, 20 min marche"
                  data-testid="journal-training"
                  className="mt-1 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                />
              </div>
              <div>
                <label className="text-sm text-slate-300">Repas de la journée</label>
                <textarea
                  rows={2}
                  value={form.meals}
                  onChange={(e) => setForm({ ...form, meals: e.target.value })}
                  placeholder="Ex : Flocons + fruits, poulet riz, skyr banane"
                  data-testid="journal-meals"
                  className="mt-1 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                />
              </div>
              <div>
                <label className="text-sm text-slate-300">Notes libres</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Ce qui t'a fait du bien, ce qui a pesé, ce dont tu es fier·e…"
                  data-testid="journal-notes"
                  className="mt-1 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <button
                onClick={save}
                data-testid="journal-save"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold px-5 py-3 rounded-full"
              >
                <Save className="w-4 h-4" /> Enregistrer
              </button>
              <button
                onClick={removeDay}
                data-testid="journal-delete"
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-rose-500/15 hover:text-rose-200 border border-white/10 text-slate-200 px-5 py-3 rounded-full"
              >
                <Trash2 className="w-4 h-4" /> Effacer ce jour
              </button>
            </div>
          </div>

          <aside className="lg:col-span-4 em-card p-6" data-testid="journal-history">
            <h4 className="text-white font-semibold font-[Outfit]">Historique récent</h4>
            <p className="text-xs text-slate-500 mt-1">14 derniers jours enregistrés.</p>
            <div className="mt-4 space-y-2 max-h-96 overflow-auto pr-1">
              {dates.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Aucune entrée pour l'instant.</p>
              ) : (
                dates.map((d) => {
                  const e = entries[d];
                  const mood = moods.find((m) => m.v === e.mood);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDate(d)}
                      className={`w-full text-left rounded-xl border px-3 py-2 ${
                        d === date ? "border-orange-500/60 bg-orange-500/10" : "border-white/10 bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white">
                          {new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                        </span>
                        {mood && <span className="text-xs text-slate-300">{mood.label}</span>}
                      </div>
                      {e.notes && (
                        <p className="mt-1 text-xs text-slate-400 line-clamp-2">{e.notes}</p>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </aside>
        </div>

        <div className="mt-6">
          <MoodChart entries={entries} />
        </div>
      </div>
    </section>
  );
}
